
import React, { useState, useEffect } from "react";
import { UserType } from "@/lib/supabase/types";
import { fetchAllUsers } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { RefreshCw } from "lucide-react";

interface UserSearchPopoverProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectUser: (user: UserType) => void;
  selectedUser: UserType | null;
}

const UserSearchPopover: React.FC<UserSearchPopoverProps> = ({
  onSelectUser,
  selectedUser
}) => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all users on component mount
  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const allUsers = await fetchAllUsers();
        setUsers(allUsers || []);
      } catch (err) {
        console.error('Error loading users:', err);
        setError('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Handle user selection
  const handleSelectUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      onSelectUser(user);
    }
  };

  if (isLoading) {
    return (
      <Button variant="outline" className="w-full justify-center" disabled>
        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
        Loading users...
      </Button>
    );
  }

  if (error) {
    return (
      <Button variant="outline" className="w-full justify-between text-destructive">
        Error loading users
      </Button>
    );
  }

  return (
    <Select
      value={selectedUser?.id}
      onValueChange={handleSelectUser}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select a user" />
      </SelectTrigger>
      <SelectContent>
        {users.length === 0 ? (
          <div className="px-2 py-4 text-center text-sm text-muted-foreground">
            No users found
          </div>
        ) : (
          users.map((user) => (
            <SelectItem key={user.id} value={user.id}>
              {user.firstName} {user.lastName} ({user.email})
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
};

export default UserSearchPopover;
